/**
 * Allowlisted HTML tags for partners.advert description (TP-516).
 * Keep in sync with sanitizePartnerDescription() in apps/api/app/Http/helpers.php.
 */
const ALLOWED_TAGS = new Set([
  'p',
  'br',
  'strong',
  'em',
  'a',
  'ul',
  'ol',
  'li',
  'small',
]);

const VOID_TAGS = new Set(['br']);

const TAG_RE = /^<\/?([a-zA-Z][a-zA-Z0-9]*)\b([^>]*)\/?>$/;

// Escape raw special chars, but keep existing named/decimal/hex entities intact
// so API-sanitised markup (e.g. `&amp;`) is not double-escaped.
const escapeAmpersands = (text) =>
  text.replace(/&(?!(?:[a-zA-Z][a-zA-Z0-9]*|#\d+|#x[0-9a-fA-F]+);)/g, '&amp;');

const escapeText = (text) =>
  escapeAmpersands(text)
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const escapeAttr = (value) =>
  escapeAmpersands(String(value))
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/**
 * Extract a safe http(s) href from a raw start-tag attribute string.
 * @param {string} attrString
 * @returns {string|null}
 */
const extractSafeHref = (attrString) => {
  if (!attrString) {
    return null;
  }

  // href="...", href='...', or href=unquoted
  const match =
    /\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(attrString) || null;
  if (!match) {
    return null;
  }

  const href = (match[1] ?? match[2] ?? match[3] ?? '').trim();
  if (!/^https?:\/\//i.test(href)) {
    return null;
  }

  return href;
};

/**
 * Sanitize partner advert description HTML to the allowlisted tags.
 * Works in browser and Node SSR without DOM APIs or extra packages.
 *
 * @param {string|null|undefined} input
 * @returns {string}
 */
export default function sanitizePartnerDescription(input) {
  if (input == null || typeof input !== 'string' || input === '') {
    return '';
  }

  let result = '';
  const openStack = [];
  let i = 0;

  while (i < input.length) {
    if (input.charAt(i) !== '<') {
      const next = input.indexOf('<', i);
      const text = next === -1 ? input.slice(i) : input.slice(i, next);
      result += escapeText(text);
      i = next === -1 ? input.length : next;
      continue;
    }

    const close = input.indexOf('>', i);
    if (close === -1) {
      result += escapeText(input.slice(i));
      break;
    }

    const rawTag = input.slice(i, close + 1);
    i = close + 1;

    // Drop comments, doctype, processing instructions, and malformed tags.
    if (
      rawTag.startsWith('<!--') ||
      rawTag.startsWith('<!') ||
      rawTag.startsWith('<?')
    ) {
      continue;
    }

    const tagMatch = TAG_RE.exec(rawTag);
    if (!tagMatch) {
      // Not a real tag (e.g. "A < B > C") — keep the text escaped.
      result += escapeText(rawTag);
      continue;
    }

    const tagName = tagMatch[1].toLowerCase();
    const attrString = tagMatch[2] || '';
    const isClosing = rawTag.charAt(1) === '/';
    const isSelfClosing = VOID_TAGS.has(tagName) || /\/\s*>$/.test(rawTag);

    if (!ALLOWED_TAGS.has(tagName)) {
      continue;
    }

    if (isClosing) {
      // Close the matching open tag; drop stray closers.
      const idx = openStack.lastIndexOf(tagName);
      if (idx === -1) {
        continue;
      }
      while (openStack.length > idx) {
        result += `</${openStack.pop()}>`;
      }
      continue;
    }

    if (tagName === 'a') {
      const href = extractSafeHref(attrString);
      if (!href) {
        // Keep link text; skip the unsafe/missing anchor tag itself.
        continue;
      }
      result += `<a href="${escapeAttr(href)}">`;
      if (!isSelfClosing) {
        openStack.push('a');
      } else {
        result += '</a>';
      }
      continue;
    }

    if (isSelfClosing || VOID_TAGS.has(tagName)) {
      result += `<${tagName}>`;
      continue;
    }

    result += `<${tagName}>`;
    openStack.push(tagName);
  }

  while (openStack.length) {
    result += `</${openStack.pop()}>`;
  }

  return result;
}
